// Dialysis sessions and their vitals (SPEC.md §4.3).
import { useDbQuery } from '../db/hooks';
import type { DbClient } from '../db/client';
import type { SessionRow, SessionStatus } from '../lib/sessions';
import type { Modality } from '../lib/schedule';

export type VitalPhase = 'pre' | 'intra' | 'post';

export interface VitalRow {
  id: string;
  session_id: string;
  recorded_at: string;
  phase: VitalPhase;
  bp_systolic: number | null;
  bp_diastolic: number | null;
  pulse: number | null;
  temperature_c: number | null;
  spo2: number | null;
  blood_sugar_mg_dl: number | null;
  notes: string | null;
}

const SESSION_COLUMNS = `id, session_date, modality, status, start_time, end_time, duration_min, pre_weight_kg, post_weight_kg,
  dry_weight_kg, uf_goal_ml, uf_achieved_ml, early_end_reason, missed_reason, notes, blood_flow_ml_min, dialysate_flow_ml_min,
  dialyzer, heparin_dose, needle_gauge, venous_pressure, arterial_pressure, tmp, conductivity`;

/** All sessions, newest first. A person logs ~150 a year, so loading them all is fine. */
export function useSessions() {
  const query = useDbQuery<SessionRow>(
    `SELECT ${SESSION_COLUMNS} FROM sessions WHERE deleted_at IS NULL ORDER BY session_date DESC, start_time DESC`,
    [],
    ['sessions']
  );
  return { sessions: query.data ?? [], isLoading: query.isLoading };
}

/** Pre and post vitals for every session, keyed by session id. */
export type VitalSummary = Pick<VitalRow, 'session_id' | 'phase' | 'bp_systolic' | 'bp_diastolic' | 'pulse'>;

export function useSessionVitalsSummary() {
  const query = useDbQuery<VitalSummary>(
    "SELECT session_id, phase, bp_systolic, bp_diastolic, pulse FROM session_vitals WHERE deleted_at IS NULL AND phase IN ('pre', 'post')",
    [],
    ['session_vitals']
  );
  const bySession = new Map<string, { pre?: VitalSummary; post?: VitalSummary }>();
  for (const row of query.data ?? []) {
    const entry = bySession.get(row.session_id) ?? {};
    entry[row.phase as 'pre' | 'post'] = row;
    bySession.set(row.session_id, entry);
  }
  return bySession;
}

export function useSession(id: string | undefined) {
  const session = useDbQuery<SessionRow>(`SELECT ${SESSION_COLUMNS} FROM sessions WHERE id = ? AND deleted_at IS NULL`, [id ?? ''], ['sessions']);
  const vitals = useDbQuery<VitalRow>(
    'SELECT id, session_id, recorded_at, phase, bp_systolic, bp_diastolic, pulse, temperature_c, spo2, blood_sugar_mg_dl, notes FROM session_vitals WHERE session_id = ? AND deleted_at IS NULL ORDER BY recorded_at',
    [id ?? ''],
    ['session_vitals']
  );
  return {
    session: session.data?.[0] ?? null,
    vitals: vitals.data ?? [],
    isLoading: session.isLoading || vitals.isLoading
  };
}

export type SessionInput = Partial<Omit<SessionRow, 'id'>>;

export function createSession(db: DbClient, values: SessionInput & { session_date: string; modality: Modality; status: SessionStatus }) {
  return db.insert<SessionRow>('sessions', values);
}

export function updateSession(db: DbClient, id: string, values: SessionInput) {
  return db.update<SessionRow>('sessions', id, values);
}

export async function deleteSession(db: DbClient, id: string, vitals: VitalRow[]) {
  for (const vital of vitals) await db.softDelete('session_vitals', vital.id);
  await db.softDelete('sessions', id);
}

export type VitalInput = Partial<Omit<VitalRow, 'id' | 'session_id' | 'phase'>>;

/** Pre and post vitals are one row each per session; this creates or updates it. */
export async function savePhaseVitals(db: DbClient, sessionId: string, phase: 'pre' | 'post', existing: VitalRow | undefined, values: VitalInput) {
  if (existing) return db.update('session_vitals', existing.id, values);
  return db.insert('session_vitals', { session_id: sessionId, phase, recorded_at: new Date().toISOString(), ...values });
}

export function addIntraVitals(db: DbClient, sessionId: string, values: VitalInput) {
  return db.insert('session_vitals', { session_id: sessionId, phase: 'intra', recorded_at: new Date().toISOString(), ...values });
}

export function removeVitals(db: DbClient, id: string) {
  return db.softDelete('session_vitals', id);
}

export function markMissed(db: DbClient, date: string, modality: Modality, reason: string | null) {
  return createSession(db, { session_date: date, modality, status: 'missed', missed_reason: reason });
}
